import React from 'react'
import PropTypes from 'prop-types'
import { t } from 'i18next'

import { Icon, Dropdown, Flex } from '@ui'
import { isDefined } from '@app/util'

const exactValueKey = (value) => `${typeof value}:${String(value)}`

export const SelectFilter = ({
  column,
  configColumn: { rowToStr, rowToFilterType, sortRowFilterTypeFn },
  handleOnCollapsed
}) => {
  const { filterValue, setFilter, preFilteredRows, id } = column ?? {}
  const options = React.useMemo(() => {
    const options = new Set(Array.isArray(filterValue) ? filterValue : [])

    preFilteredRows.forEach((row) => {
      const option = rowToFilterType
        ? rowToFilterType(row.original)
        : (rowToStr?.(row.original) ?? null)

      if (Array.isArray(option)) {
        option.forEach((item) => options.add(item))
      } else {
        options.add(option)
      }
    })

    return [...options.values()]
  }, [filterValue, id, preFilteredRows, rowToFilterType, rowToStr])

  const transferForDropdown = (array) => {
    const toDropdownOption = (option) => {
      if (
        typeof option === 'string' ||
        typeof option === 'number' ||
        typeof option === 'boolean' ||
        option === null
      ) {
        return {
          id: `${option}`,
          label: option,
          value: option,
          __isObjectOption: false
        }
      }

      if (!option) return null

      return {
        id: `${option.value}`,
        searchableParam: option.searchableParam,
        label: option.label ?? option.value,
        value: option.value,
        __isObjectOption: true
      }
    }

    const uniqueByValue = new Map()

    if (Array.isArray(array)) {
      array.forEach((item) => {
        const dropdownOption = toDropdownOption(item)
        if (!dropdownOption) return

        const key = exactValueKey(dropdownOption.value)
        const existing = uniqueByValue.get(key)

        if (!existing) {
          uniqueByValue.set(key, dropdownOption)
          return
        }

        if (!existing.__isObjectOption && dropdownOption.__isObjectOption) {
          uniqueByValue.set(key, dropdownOption)
        }
      })
    }

    return [...uniqueByValue.values()]
      .map(({ __isObjectOption, ...option }) => option)
      .sort((a, b) => sortRowFilterTypeFn?.(a, b) ?? 0)
  }

  const transferredOptions = transferForDropdown(options)
  const selectedValueKeys = new Set(
    (Array.isArray(filterValue) ? filterValue : []).map((value) => exactValueKey(value))
  )

  return (
    <Flex direction={Flex.DIRECTION.COLUMN}>
      <Dropdown
        cb={handleOnCollapsed || undefined}
        type={Dropdown.TYPES.VARIABLE}
        style={Dropdown.STYLES.LIGHT}
        size={Dropdown.SIZES.LARGE}
        options={transferredOptions}
        value={transferredOptions.filter((option) =>
          selectedValueKeys.has(exactValueKey(option.value))
        )}
        onChange={(items) => {
          const values = (items ?? []).map((item) => item.value)
          const dedupedValues = [
            ...new Map(values.map((value) => [exactValueKey(value), value])).values()
          ]
          setFilter(dedupedValues.length ? dedupedValues : null)
        }}
        placeholder={t('SEARCH')}
        icon={<Icon ico='search' size={Icon.SIZES.SMALL} />}
        searchable
        disableSort={isDefined(sortRowFilterTypeFn)}
      />
    </Flex>
  )
}

SelectFilter.propTypes = {
  setAllFilters: PropTypes.func.isRequired,
  filterValue: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
  ),
  handleOnCollapsed: PropTypes.func
}
SelectFilter.defaultProps = {
  rowToString: () => null,
  rowToFilterType: null,
  filterValue: [],
  handleOnCollapsed: null
}
